import styled from 'styled-components'

import PaymentType from 'shared/enums/PaymentType'
import {
  BillingInfo,
  AfterPay,
  Affirm,
  CreditCard,
  PayPal,
  Klarna,
} from 'account/models/BillingInfo'
import { CardIcon, AfterPayIcon, AffirmIcon, PayPalIcon, KlarnaIcon } from 'shared/components/Icons'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { SavedCardPaymentOption } from 'checkout/panels/PaymentMethod/SavedCardPaymentOption'

type PaymentSummaryProps = {
  billingInfo: BillingInfo
}

const PaymentSummary = ({ billingInfo }: PaymentSummaryProps) => {
  const isExposedPaymentMethods = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_EXPOSED_PAYMENT_METHODS)

  if (isCreditCard(billingInfo)) {
    const { cardBrand, cardExpMonth, cardExpYear, last4Digits } = billingInfo
    if (isExposedPaymentMethods) {
      return (
        <SavedCardPaymentOption
          cardBrand={cardBrand}
          cardExpMonth={cardExpMonth}
          cardExpYear={cardExpYear}
          last4Digits={last4Digits}
        />
      )
    }
    return (
      <SummaryBox>
        {cardBrand.toUpperCase()} *{last4Digits}
        <CardIcon brand={cardBrand} width={26} height={16} />
      </SummaryBox>
    )
  }
  if (isAfterPay(billingInfo)) {
    return (
      <SummaryBox>
        {billingInfo.email}
        <AfterPayIcon />
      </SummaryBox>
    )
  }
  if (isAffirm(billingInfo)) {
    return (
      <SummaryBox>
        {billingInfo.email}
        <AffirmIcon />
      </SummaryBox>
    )
  }
  if (isPayPal(billingInfo)) {
    return (
      <SummaryBox>
        {billingInfo.email}
        <PayPalIcon />
      </SummaryBox>
    )
  }
  if (isPayPalv2(billingInfo)) {
    return (
      <SummaryBox>
        <PayPalIcon />
      </SummaryBox>
    )
  }
  if (isKlarna(billingInfo)) {
    return (
      <SummaryBox>
        {billingInfo.email}
        <KlarnaIcon />
      </SummaryBox>
    )
  }
  return null
}

const isCreditCard = (billingInfo: BillingInfo): billingInfo is CreditCard =>
  billingInfo.paymentType === PaymentType.CreditCard
const isAffirm = (billingInfo: BillingInfo): billingInfo is Affirm =>
  billingInfo.paymentType === PaymentType.Affirm
const isAfterPay = (billingInfo: BillingInfo): billingInfo is AfterPay =>
  billingInfo.paymentType === PaymentType.AfterPay
const isKlarna = (billingInfo: BillingInfo): billingInfo is Klarna =>
  billingInfo.paymentType === PaymentType.Klarna
const isPayPal = (billingInfo: BillingInfo): billingInfo is PayPal =>
  billingInfo.paymentType === PaymentType.PayPal
const isPayPalv2 = (billingInfo: BillingInfo): billingInfo is PayPal =>
  billingInfo.paymentType === PaymentType.PayPalv2

const SummaryBox = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

export default PaymentSummary
